/**
 * EditProfileSection - Edit display name, phone number and password
 * Self-contained: owns its editing state and Firestore/Auth update handlers
 */

import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { doc, updateDoc, Timestamp } from 'firebase/firestore';
import { EmailAuthProvider, updatePassword, reauthenticateWithCredential, User as FirebaseUser } from 'firebase/auth';
import { Lock } from 'lucide-react';
import { db } from '@/lib/firebase';
import { toast } from '@/lib/toast';
import { User } from '@/types';

interface EditProfileSectionProps {
  user: User;
  firebaseUser: FirebaseUser | null;
  hasPasswordLinked: boolean | undefined;
}

export default function EditProfileSection({ user, firebaseUser, hasPasswordLinked }: EditProfileSectionProps) {
  const { t } = useTranslation();

  // State for editing profile (name and password)
  const [editingName, setEditingName] = useState(false);
  const [newDisplayName, setNewDisplayName] = useState(user?.displayName || '');
  const [savingName, setSavingName] = useState(false);

  const [editingPassword, setEditingPassword] = useState(false);
  const [currentPassword, setCurrentPassword] = useState('');
  const [newPassword, setNewPassword] = useState('');
  const [confirmNewPassword, setConfirmNewPassword] = useState('');
  const [savingPassword, setSavingPassword] = useState(false);

  // State for editing phone
  const [editingPhone, setEditingPhone] = useState(false);
  const [newPhone, setNewPhone] = useState(user?.phoneNumber || '');
  const [savingPhone, setSavingPhone] = useState(false);

  // Handle name change
  const handleSaveName = async () => {
    if (!firebaseUser || !newDisplayName.trim()) {
      toast.error(t('profilePage.nameEmpty'));
      return;
    }

    setSavingName(true);
    try {
      await updateDoc(doc(db, 'users', firebaseUser.uid), {
        displayName: newDisplayName.trim(),
        updatedAt: Timestamp.now(),
      });
      toast.success(t('profilePage.nameUpdated'));
      setEditingName(false);
      // Reload to get updated data
      window.location.reload();
    } catch (error) {
      console.error('Error updating name:', error);
      toast.error(t('profilePage.nameUpdateError'));
    } finally {
      setSavingName(false);
    }
  };

  // Handle phone change
  const handleSavePhone = async () => {
    if (!firebaseUser) return;

    const trimmed = newPhone.trim();
    if (!trimmed) {
      toast.error(t('profilePage.phoneEmpty', 'Introduce un número de teléfono'));
      return;
    }

    const phoneRegex = /^\+?[0-9\s().-]{6,20}$/;
    if (!phoneRegex.test(trimmed)) {
      toast.error(t('profilePage.phoneInvalid', 'Número de teléfono no válido'));
      return;
    }

    setSavingPhone(true);
    try {
      await updateDoc(doc(db, 'users', firebaseUser.uid), {
        phoneNumber: trimmed,
        updatedAt: Timestamp.now(),
      });
      toast.success(t('profilePage.phoneUpdated', 'Teléfono actualizado'));
      setEditingPhone(false);
      window.location.reload();
    } catch (error) {
      console.error('Error updating phone:', error);
      toast.error(t('profilePage.phoneUpdateError', 'Error al actualizar el teléfono'));
    } finally {
      setSavingPhone(false);
    }
  };

  // Handle password change
  const handleSavePassword = async () => {
    if (!firebaseUser || !firebaseUser.email) {
      toast.error(t('profilePage.noActiveSession'));
      return;
    }

    if (newPassword.length < 6) {
      toast.error(t('profilePage.passwordTooShort'));
      return;
    }

    if (newPassword !== confirmNewPassword) {
      toast.error(t('profilePage.passwordsDoNotMatch'));
      return;
    }

    setSavingPassword(true);
    try {
      // Re-authenticate user first
      const credential = EmailAuthProvider.credential(firebaseUser.email, currentPassword);
      await reauthenticateWithCredential(firebaseUser, credential);

      // Update password
      await updatePassword(firebaseUser, newPassword);

      toast.success(t('profilePage.passwordUpdated'));
      setEditingPassword(false);
      setCurrentPassword('');
      setNewPassword('');
      setConfirmNewPassword('');
    } catch (error: any) {
      console.error('Error updating password:', error);
      if (error.code === 'auth/wrong-password') {
        toast.error(t('profilePage.wrongCurrentPassword'));
      } else if (error.code === 'auth/weak-password') {
        toast.error(t('profilePage.weakPassword'));
      } else {
        toast.error(t('profilePage.passwordUpdateError'));
      }
    } finally {
      setSavingPassword(false);
    }
  };

  return (
    <div className="bg-surface border border-hairline rounded-xl p-6 mb-6">
      <h2 className="font-sans text-lg font-semibold text-ink mb-4">{t('profilePage.editProfile')}</h2>

      <div className="space-y-4">
        {/* Edit Display Name */}
        <div className="p-4 border border-gray-200 dark:border-gray-700 rounded-lg">
          <div className="flex items-center justify-between mb-2">
            <p className="font-medium text-ink">{t('profilePage.nameLabel')}</p>
            {!editingName && (
              <button
                onClick={() => {
                  setNewDisplayName(user?.displayName || '');
                  setEditingName(true);
                }}
                className="text-accent text-sm hover:underline"
              >
                {t('profilePage.edit')}
              </button>
            )}
          </div>

          {editingName ? (
            <div className="space-y-3">
              <input
                type="text"
                value={newDisplayName}
                onChange={(e) => setNewDisplayName(e.target.value)}
                placeholder={t('profilePage.yourName')}
                className="w-full px-4 py-2 border border-hairline rounded-lg bg-surface-3 text-ink placeholder:text-ink-subtle focus:ring-2 focus:ring-focus focus:border-transparent"
              />
              <div className="flex gap-2">
                <button
                  onClick={handleSaveName}
                  disabled={savingName}
                  className="px-4 py-2 bg-accent text-on-accent text-sm font-medium rounded-lg hover:bg-accent-hover transition-colors disabled:opacity-50"
                >
                  {savingName ? t('profilePage.saving') : t('common.save')}
                </button>
                <button
                  onClick={() => setEditingName(false)}
                  className="px-4 py-2 text-ink-muted hover:text-ink text-sm hover:bg-surface-2 rounded-lg transition-colors"
                >
                  {t('common.cancel')}
                </button>
              </div>
            </div>
          ) : (
            <p className="text-ink-muted">{user?.displayName || t('profilePage.noName')}</p>
          )}
        </div>

        {/* Edit Phone Number */}
        <div className="p-4 border border-gray-200 dark:border-gray-700 rounded-lg">
          <div className="flex items-center justify-between mb-2">
            <p className="font-medium text-ink">
              {t('profilePage.phoneLabel', 'Teléfono')}
            </p>
            {!editingPhone && (
              <button
                onClick={() => {
                  setNewPhone(user?.phoneNumber || '');
                  setEditingPhone(true);
                }}
                className="text-accent text-sm hover:underline"
              >
                {user?.phoneNumber ? t('profilePage.edit') : t('profilePage.add', 'Añadir')}
              </button>
            )}
          </div>

          {editingPhone ? (
            <div className="space-y-3">
              <input
                type="tel"
                inputMode="tel"
                autoComplete="tel"
                value={newPhone}
                onChange={(e) => setNewPhone(e.target.value)}
                placeholder={t('profilePage.phonePlaceholder', 'Ej: +34 612 345 678')}
                className="w-full px-4 py-2 border border-hairline rounded-lg bg-surface-3 text-ink placeholder:text-ink-subtle focus:ring-2 focus:ring-focus focus:border-transparent"
              />
              <div className="flex gap-2">
                <button
                  onClick={handleSavePhone}
                  disabled={savingPhone}
                  className="px-4 py-2 bg-accent text-on-accent text-sm font-medium rounded-lg hover:bg-accent-hover transition-colors disabled:opacity-50"
                >
                  {savingPhone ? t('profilePage.saving') : t('common.save')}
                </button>
                <button
                  onClick={() => setEditingPhone(false)}
                  className="px-4 py-2 text-ink-muted hover:text-ink text-sm hover:bg-surface-2 rounded-lg transition-colors"
                >
                  {t('common.cancel')}
                </button>
              </div>
              <p className="inline-flex items-center gap-1.5 text-xs text-ink-subtle">
                <Lock className="w-3 h-3" aria-hidden="true" /> {t('profilePage.phonePrivacyNote', 'Solo el barbero asignado a tu turno podrá verlo')}
              </p>
            </div>
          ) : (
            <p className="text-ink-muted">
              {user?.phoneNumber || t('profilePage.phoneMissing', 'Sin teléfono guardado')}
            </p>
          )}
        </div>

        {/* Change Password - Only show if user has password provider */}
        {hasPasswordLinked && (
          <div className="p-4 border border-hairline rounded-lg bg-surface-2">
            <div className="flex items-center justify-between mb-2">
              <p className="font-medium text-ink">{t('profilePage.password')}</p>
              {!editingPassword && (
                <button
                  onClick={() => setEditingPassword(true)}
                  className="text-accent text-sm hover:underline"
                >
                  {t('profilePage.change')}
                </button>
              )}
            </div>

            {editingPassword ? (
              <div className="space-y-3">
                <div>
                  <label className="block text-sm text-ink-muted mb-1">{t('profilePage.currentPassword')}</label>
                  <input
                    type="password"
                    value={currentPassword}
                    onChange={(e) => setCurrentPassword(e.target.value)}
                    placeholder={t('profilePage.currentPasswordPlaceholder')}
                    className="w-full px-4 py-2 border border-hairline rounded-lg bg-surface-3 text-ink placeholder:text-ink-subtle focus:ring-2 focus:ring-focus focus:border-transparent"
                  />
                </div>
                <div>
                  <label className="block text-sm text-ink-muted mb-1">{t('profilePage.newPassword')}</label>
                  <input
                    type="password"
                    value={newPassword}
                    onChange={(e) => setNewPassword(e.target.value)}
                    placeholder={t('profilePage.minChars')}
                    className="w-full px-4 py-2 border border-hairline rounded-lg bg-surface-3 text-ink placeholder:text-ink-subtle focus:ring-2 focus:ring-focus focus:border-transparent"
                  />
                </div>
                <div>
                  <label className="block text-sm text-ink-muted mb-1">{t('profilePage.confirmNewPassword')}</label>
                  <input
                    type="password"
                    value={confirmNewPassword}
                    onChange={(e) => setConfirmNewPassword(e.target.value)}
                    placeholder={t('profilePage.repeatNewPassword')}
                    className="w-full px-4 py-2 border border-hairline rounded-lg bg-surface-3 text-ink placeholder:text-ink-subtle focus:ring-2 focus:ring-focus focus:border-transparent"
                  />
                </div>
                <div className="flex gap-2">
                  <button
                    onClick={handleSavePassword}
                    disabled={savingPassword}
                    className="px-4 py-2 bg-accent text-on-accent text-sm font-medium rounded-lg hover:bg-accent-hover transition-colors disabled:opacity-50"
                  >
                    {savingPassword ? t('profilePage.saving') : t('profilePage.changePassword')}
                  </button>
                  <button
                    onClick={() => {
                      setEditingPassword(false);
                      setCurrentPassword('');
                      setNewPassword('');
                      setConfirmNewPassword('');
                    }}
                    className="px-4 py-2 text-ink-muted hover:text-ink text-sm hover:bg-surface-2 rounded-lg transition-colors"
                  >
                    {t('common.cancel')}
                  </button>
                </div>
              </div>
            ) : (
              <p className="text-ink-muted">••••••••</p>
            )}
          </div>
        )}
      </div>
    </div>
  );
}
